import React from "react";
import { Box, Typography, Skeleton, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useGetPopularPlayersQuery } from "../../store/api/mlbApi";
import PlayerHeadshot from "../PlayerHeadshot";
import PlayerSparkline from "./PlayerSparkline";
import HomeSection from "./HomeSection";
import { formatPlayerName } from "../../utils/textUtils";
import { homeCardHover, horizontalScroll } from "./homeStyles";
import { colors } from "../../theme";

const HotPlayerCard = ({ player, onClick }) => {
  const career = player.stats?.hitting?.career;
  const homeruns = career?.homeruns ?? player.homerunsNumeric ?? "–";
  const team = career?.team;

  return (
    <Box
      onClick={onClick}
      sx={{
        minWidth: 140,
        maxWidth: 140,
        flex: "0 0 auto",
        p: 1.5,
        borderRadius: 1.5,
        border: `1px solid ${colors.border}`,
        ...homeCardHover,
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
        <PlayerHeadshot
          playerId={player.playerId}
          size={56}
          showTooltip={false}
          showLoading={false}
        />
      </Box>
      <Typography variant="body2" fontWeight={600} noWrap textAlign="center" title={formatPlayerName(player)}>
        {formatPlayerName(player)}
      </Typography>
      {team && (
        <Typography variant="caption" color="text.secondary" noWrap display="block" textAlign="center">
          {team}
        </Typography>
      )}
      <Box sx={{ mt: 1, textAlign: "center" }}>
        <Typography variant="caption" color="text.secondary">
          Career HR
        </Typography>
        <Typography variant="h6" fontWeight={700} color="primary.main" lineHeight={1.2}>
          {homeruns}
        </Typography>
      </Box>
      <Box sx={{ mt: 1 }}>
        <PlayerSparkline seasons={player.stats?.hitting?.seasons} />
      </Box>
    </Box>
  );
};

/**
 * All-time HR leaders row — popular players as debate starters until trending API ships.
 * TODO(Phase 4): Swap to useGetTrendingPlayersQuery for real-time popular comparisons.
 */
const HotPlayersRow = () => {
  const navigate = useNavigate();
  const { data: players, isLoading, isError } = useGetPopularPlayersQuery("homeruns");

  return (
    <HomeSection
      title="All-time HR leaders"
      subtitle="Career home run kings — tap a legend, then compare"
      noPadding
    >
      <Box sx={{ p: 2 }}>
        {isLoading && (
          <Box sx={horizontalScroll}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} variant="rounded" width={140} height={180} sx={{ flexShrink: 0 }} />
            ))}
          </Box>
        )}

        {isError && (
          <Alert severity="warning">Could not load popular players</Alert>
        )}

        {!isLoading && !isError && (!players || players.length === 0) && (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
            No player data available
          </Typography>
        )}

        {!isLoading && !isError && players?.length > 0 && (
          <Box sx={horizontalScroll}>
            {players.map((player) => (
              <HotPlayerCard
                key={player.playerId}
                player={player}
                onClick={() => navigate(`/app/player/${player.playerId}`)}
              />
            ))}
          </Box>
        )}
      </Box>
    </HomeSection>
  );
};

export default HotPlayersRow;
